// How football's scoring reads as a table, and what the create form asks
// about it (PLAN-2026-10-18-scoring-anyone-can-read §2.1, §2.2).
//
// A SECTION PER POSITION, its main kinds of play first, in the order that
// position earns its points: a quarterback passes and then runs, a running
// back runs and then catches, a receiver catches. What a position can score
// but rarely does (a quarterback's catch, a return) sits under its section's
// "more" line rather than being left out, so every rule is reachable.
//
// TWO QUESTIONS, NO SETUP NAMES. Setup asks whether a catch scores and
// whether the league starts a team's defense or defensive players, never
// both (DIRECTION A12), rather than offering PPR, Half-PPR and the rest by
// the names experienced players use. Anything else is a number in the table.

import type { ScoringLayout, ScoringSetup } from '@fsp/sports-core';

export const footballScoringLayout: ScoringLayout = {
  groups: [
    {
      id: 'passing',
      title: 'Passing',
      stats: [
        'pass_td',
        'pass_yd',
        'pass_int',
        'pass_2pt',
        'pass_cmp',
        'pass_inc',
        'pass_att',
        'pass_sack',
        'pass_fd',
      ],
    },
    {
      id: 'running',
      title: 'Running',
      stats: ['rush_td', 'rush_yd', 'rush_2pt', 'rush_att', 'rush_fd'],
    },
    {
      id: 'catching',
      title: 'Catching',
      stats: ['rec', 'rec_yd', 'rec_td', 'rec_2pt', 'rec_tgt', 'rec_fd'],
    },
    { id: 'fumbles', title: 'Fumbles', stats: ['fum_lost', 'fum'] },
    { id: 'returns', title: 'Returns', stats: ['ret_td', 'fum_rec_td', 'ret_yd'] },
    {
      id: 'kicking',
      title: 'Kicking',
      stats: [
        'fg_0_19',
        'fg_20_29',
        'fg_30_39',
        'fg_40_49',
        'fg_50p',
        'fg_miss',
        'xp_made',
        'xp_miss',
      ],
    },
    {
      id: 'defense',
      title: 'Team defense',
      stats: [
        'dst_sack',
        'dst_int',
        'dst_fum_rec',
        'dst_ff',
        'dst_td',
        'dst_ret_td',
        'dst_safety',
        'dst_blk',
        'dst_pts_allowed',
        'dst_yds_allowed',
      ],
    },
    // Defensive players (Q11), in four kinds of play so each position reads
    // its own first: a lineman rushes the passer, a back covers.
    { id: 'tackling', title: 'Tackling', stats: ['idp_tkl_solo', 'idp_tkl_ast', 'idp_tkl_loss'] },
    { id: 'pass-rush', title: 'Rushing the passer', stats: ['idp_sack', 'idp_qb_hit'] },
    { id: 'coverage', title: 'Coverage', stats: ['idp_int', 'idp_pass_def'] },
    {
      id: 'takeaways',
      title: 'Takeaways and scores',
      stats: ['idp_ff', 'idp_fum_rec', 'idp_td', 'idp_safety', 'idp_blk'],
    },
  ],
  sections: [
    {
      position: 'QB',
      title: 'Quarterbacks',
      main: ['passing', 'running', 'fumbles'],
      always: ['pass_td', 'pass_yd', 'pass_int', 'rush_td', 'rush_yd', 'fum_lost'],
    },
    {
      position: 'RB',
      title: 'Running backs',
      main: ['running', 'catching', 'fumbles'],
      always: ['rush_td', 'rush_yd', 'rec', 'rec_yd', 'rec_td', 'fum_lost'],
    },
    {
      position: 'WR',
      title: 'Wide receivers',
      main: ['catching', 'fumbles'],
      always: ['rec', 'rec_yd', 'rec_td', 'fum_lost'],
    },
    {
      position: 'TE',
      title: 'Tight ends',
      main: ['catching', 'fumbles'],
      always: ['rec', 'rec_yd', 'rec_td', 'fum_lost'],
    },
    {
      position: 'K',
      title: 'Kickers',
      main: ['kicking'],
      always: ['fg_0_19', 'fg_20_29', 'fg_30_39', 'fg_40_49', 'fg_50p', 'xp_made'],
    },
    {
      position: 'DST',
      title: 'Team defense',
      main: ['defense'],
      always: ['dst_sack', 'dst_int', 'dst_fum_rec', 'dst_td', 'dst_pts_allowed'],
    },
    {
      position: 'DL',
      title: 'Defensive linemen',
      main: ['pass-rush', 'tackling', 'takeaways'],
      always: ['idp_sack', 'idp_tkl_solo', 'idp_tkl_ast', 'idp_tkl_loss'],
    },
    {
      position: 'LB',
      title: 'Linebackers',
      main: ['tackling', 'pass-rush', 'coverage', 'takeaways'],
      always: ['idp_tkl_solo', 'idp_tkl_ast', 'idp_sack', 'idp_int'],
    },
    {
      position: 'DB',
      title: 'Defensive backs',
      main: ['coverage', 'tackling', 'takeaways'],
      always: ['idp_int', 'idp_pass_def', 'idp_tkl_solo', 'idp_tkl_ast'],
    },
  ],
  series: [{ id: 'fg', label: 'Field goal, {range} yards' }],
};

export const footballScoringSetup: ScoringSetup = {
  questions: [
    {
      id: 'catches',
      label: 'Points for catches',
      style: 'switch',
      default: 'on',
      options: [
        {
          value: 'on',
          label: 'On',
          sentence: 'Every catch is worth a point, on top of its yards.',
          phrase: 'points for catches',
          available: true,
        },
        {
          value: 'off',
          label: 'Off',
          sentence: 'A catch earns its yards and any touchdown, nothing more.',
          phrase: 'no points for catches',
          available: true,
        },
      ],
    },
    {
      id: 'defense',
      label: 'Defense',
      style: 'choice',
      default: 'team',
      options: [
        {
          value: 'team',
          label: 'A team’s defense',
          sentence:
            'Each team starts one NFL team’s whole defense, which scores for sacks, takeaways and holding the other side down.',
          phrase: 'a team’s defense',
          available: true,
        },
        {
          value: 'players',
          label: 'Defensive players',
          sentence:
            'Draft linemen, linebackers and defensive backs one by one, instead of a whole team’s defense.',
          phrase: 'defensive players',
          // Playable since 2026-10-18 (Q11, PLAN-2026-10-18-defensive-players).
          available: true,
        },
      ],
    },
  ],
  presets: (answers) => {
    const catches = answers['catches'] !== 'off';
    if (answers['defense'] === 'players')
      return { scoring: catches ? 'ppr-idp' : 'standard-idp', roster: 'idp' };
    return { scoring: catches ? 'ppr' : 'standard', roster: 'standard' };
  },
};
